import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpInternalService } from 'src/app/data/service/http.service';

/** A paid price the upgrade modal can offer (api/billing/plans). */
export interface BillingPrice {
  priceId: string;
  productName: string;
  description: string | null;
  amount: number;
  currency: string;
  interval: string;
}

export interface BillingFeature {
  code: string;
  name: string;
  description: string | null;
}

export interface BillingTier {
  code: string;
  name: string;
  rank: number;
  isCurrent: boolean;
  features: BillingFeature[];
  prices: BillingPrice[];
}

export interface BillingPlans {
  currentPlan: { code: string; name: string; source: string; rank: number; expiresUtc: string | null };
  /** Organisation SuperAdmin/Admin: may buy or change the plan. Submitters may only look. */
  canManageBilling: boolean;
  /** SD-1229: the plan is managed by Synced; the modal offers a way to contact support instead. */
  managed?: boolean;
  hasActiveSubscription: boolean;
  currentPriceId: string | null;
  tiers: BillingTier[];
}

export interface CheckoutSession {
  sessionId: string;
  clientSecret: string;
}

export interface CheckoutStatus {
  status: 'open' | 'complete' | 'expired' | string;
  paymentStatus: string;
  planCode: string;
  planSource: string;
}

export type BillingStatus = 'free' | 'active' | 'past_due' | 'cancelling' | 'managed';

export interface BillingSummary {
  /**
   * amount is the price before tax; total is what Stripe charges each interval, with the tax
   * from the subscription's own tax rates (taxName e.g. 'GST', null when none applies).
   * total/tax are absent from an API that predates them.
   */
  plan: {
    code: string; name: string; amount: number; currency: string; interval: string | null; status: BillingStatus; source: string;
    tax?: number; taxPercent?: number; taxName?: string | null; total?: number;
  };
  renewsOn: string | null;
  cancelsOn: string | null;
  paymentMethod: { brand: string; last4: string; expMonth: number; expYear: number } | null;
  canManageBilling: boolean;
  hasCustomer: boolean;
  /** SD-1229: a plan Synced manages and invoices itself -- no checkout, change or cancel here. */
  managed?: boolean;
  /** When the agreed plan runs to, if Synced set an end date. */
  managedUntil?: string | null;
}

export interface BillingInvoice {
  id: string;
  number: string | null;
  created: string;
  description: string | null;
  amount: number;
  currency: string;
  status: 'paid' | 'open' | 'void' | 'uncollectible' | string;
  pdfUrl: string | null;
  hostedUrl: string | null;
}

export interface ChangePlanPreview {
  amountDue: number;
  currency: string;
  tierCode: string;
  priceAmount: number;
  interval: string;
}

/** A document the usage-limit gate turned away, waiting on Settings > Billing (api/held-documents). */
export interface HeldDocumentItem {
  id: string;
  fileName: string | null;
  contentType: string | null;
  source: string;
  fromEmail: string | null;
  subject: string | null;
  heldAtUtc: string;
  status: 'Held' | 'Processing' | 'Processed' | 'Failed' | 'Discarded' | string;
  error: string | null;
}

export interface HeldDocumentSummary {
  count: number;
  estimatedCreditsPerDocument: number;
  estimatedCreditsTotal: number;
  creditsUsed: number;
  /** null = unlimited */
  creditLimit: number | null;
  creditLimitUnlimited: boolean;
  /** null = unlimited */
  creditsRemaining: number | null;
  /** How many of the waiting documents the gate would let through right now, in order. */
  documentsProcessableNow: number;
  periodStart: string;
  periodEnd: string;
}

export interface HeldDocumentQueue {
  summary: HeldDocumentSummary;
  items: HeldDocumentItem[];
}

export interface HeldDocumentProcessResult {
  processed: { heldDocumentId: string; invoiceId: string; fileName: string | null }[];
  failed: { heldDocumentId: string; fileName: string | null; error: string }[];
  /** The gate refused a document part-way: the rest stay held. */
  stoppedAtLimit: boolean;
  remaining: number;
  summary: HeldDocumentSummary;
}

/**
 * SD-1229: the upgrade modal's calls (BillingCheckoutController). Subscribing goes through
 * Stripe Embedded Checkout, so nothing here ever carries card details.
 */
@Injectable({ providedIn: 'root' })
export class BillingService {
  constructor(private http: HttpInternalService) { }

  plans(organizationId: string): Observable<BillingPlans> {
    return this.http.getRequest<BillingPlans>('billing/plans', { organizationId });
  }

  createCheckoutSession(organizationId: string, priceId: string, reason?: string): Observable<CheckoutSession> {
    return this.http.postRequest<CheckoutSession>('billing/checkout-session', { organizationId, priceId, reason });
  }

  checkoutStatus(sessionId: string, organizationId: string): Observable<CheckoutStatus> {
    return this.http.getRequest<CheckoutStatus>(
      'billing/checkout-session/' + encodeURIComponent(sessionId) + '/status', { organizationId });
  }

  previewChangePlan(organizationId: string, priceId: string): Observable<ChangePlanPreview> {
    return this.http.postRequest<ChangePlanPreview>('billing/change-plan/preview', { organizationId, priceId });
  }

  changePlan(organizationId: string, priceId: string): Observable<{ success: boolean; planCode: string }> {
    return this.http.postRequest<{ success: boolean; planCode: string }>('billing/change-plan', { organizationId, priceId });
  }

  /** SD-1229: the plan card in one call -- plan, status, renewal/cancel date, card. */
  summary(organizationId: string): Observable<BillingSummary> {
    return this.http.getRequest<BillingSummary>('billing/summary', { organizationId });
  }

  invoices(organizationId: string): Observable<BillingInvoice[]> {
    return this.http.getRequest<BillingInvoice[]>('billing/invoices', { organizationId });
  }

  /** A Stripe Customer Portal session: card, billing details, invoices. Returns its URL. */
  portalSession(organizationId: string): Observable<{ url: string }> {
    return this.http.postRequest<{ url: string }>('billing/portal-session', { organizationId });
  }

  /** Cancel at the end of the paid period. */
  cancel(organizationId: string): Observable<{ cancelsOn: string | null; renewsOn: string | null }> {
    return this.http.postRequest<any>('billing/cancel', { organizationId });
  }

  /** Keep a plan that was set to cancel. */
  resume(organizationId: string): Observable<{ cancelsOn: string | null; renewsOn: string | null }> {
    return this.http.postRequest<any>('billing/resume', { organizationId });
  }

  // ── Held documents: the queue the usage-limit gate builds ─────────────────

  heldDocuments(organisationId: string): Observable<HeldDocumentQueue> {
    return this.http.getRequest<HeldDocumentQueue>('held-documents', { organisationId });
  }

  /** Replays the queue in order (or just the given documents), stopping at the first the gate refuses. */
  processHeldDocuments(organisationId: string, heldDocumentIds?: string[], max?: number): Observable<HeldDocumentProcessResult> {
    return this.http.postRequest<HeldDocumentProcessResult>('held-documents/process', { organisationId, heldDocumentIds: heldDocumentIds || [], max });
  }

  discardHeldDocuments(organisationId: string, heldDocumentIds: string[]): Observable<{ discarded: number; remaining: number; summary: HeldDocumentSummary }> {
    return this.http.postRequest<any>('held-documents/discard', { organisationId, heldDocumentIds });
  }
}
